import * as React from 'react';
import { asCurrencyFormat, asPercentageValue } from '../../utils/parse';
import { GroupServiceGetResponse, RateReliefCalculatorServiceResponse } from '../../services/api';
import { APIStatus } from '../../utils/types';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { fetchGroupCalculationForPeriod } from '../../store/group';
import { sub } from 'date-fns';
import { flattenTimePeriodDate } from '../../utils/date';
import { isGroupHighRisk, isReliefAvailableForGroup } from '../../utils/groupList';

export default function ReliefCalculator({ group, rates, status }: { group: GroupServiceGetResponse, rates: RateReliefCalculatorServiceResponse, status: APIStatus | null }) {
  if (!rates) return null;

  const { timePeriodDate } = group.policyDetailModel!;
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [previousRate, setPreviousRates] = React.useState<RateReliefCalculatorServiceResponse>();
  const [visibleRatesIndex, setVisibleRatesIndex] = React.useState<number>(0);

  const previousUploadId = sub(flattenTimePeriodDate(timePeriodDate!), { years: 1 });
  const availableYears = [
    new Date(timePeriodDate!).getFullYear(),
    new Date(previousUploadId!).getFullYear(),
  ];

  const availableRates = React.useMemo(() => {
    return [
      rates,
      previousRate
    ].filter((v) => v);
  }, [rates, previousRate]);

  React.useEffect(() => {
    fetchPreviousRates();
  }, []);

  async function fetchPreviousRates() {
    setIsLoading(true);
    
    try {
      if (previousUploadId) {
        const res = await fetchGroupCalculationForPeriod({
          policyNumber: group.policyDetailModel?.policyNumber!,
          timePeriodDate: previousUploadId,
        });

        setPreviousRates(res);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const hasReliefAvailable = React.useMemo<boolean>(() => {
    return !!group.policyDetailModel?.eligibleRateRelief && (rates.optionsEligible === "Yes" || (isReliefAvailableForGroup(group) && !isGroupHighRisk(group)));
  }, [rates, group]);

  const showLoadingState = React.useMemo(() => {
    return !!isLoading || status === APIStatus.LOADING;
  }, [isLoading, status]);

  return (
    <section className="grid gap-4 p-6 rounded bg-faded-blue">
      <h3 className="flex items-center gap-1 text-lg font-bold ">
        <span className="pi pi-calculator text-allstate-blue"></span> <span>Rate relief calculator</span>
      </h3>

      <div className="relative">
        {showLoadingState && (
          <div className="absolute flex items-center justify-center w-full h-full">
            <ProgressSpinner className="w-8 h-8 " />
          </div>
        )}

        <div
          className={classNames(
            "grid grid-cols-[1fr_auto] items-center gap-2 p-4 bg-white rounded transition-opacity",
            {
              'opacity-25': showLoadingState
            }
          )}>

          {availableYears.length > 1 && availableYears.length === availableRates.length && (
            <div className="grid grid-cols-2 col-span-2 gap-2 mb-4">
              {availableYears.map((year, index) => {
                return (
                  <div
                    key={year}
                    className={
                      classNames(
                        'text-center p-2 pt-0 font-bold text-lg cursor-pointer',
                        {
                          'border-allstate-light-blue border-b-4': visibleRatesIndex === index,
                          'border-grey-7 border-b-4 text-grey-7': visibleRatesIndex !== index
                        }
                      )
                    }
                    onClick={() => setVisibleRatesIndex(index)}
                  >{year}</div>
                )
              })}
            </div>
          )}

          
          <div>Current prem EQ:</div>
          <div className="font-bold text-right">
            {asCurrencyFormat(group.policyDetailModel?.currentPremiumEquivalent || 0)}
          </div>

          <div>Current increase:</div>
          <div className="font-bold text-right">
            {asPercentageValue((group.policyDetailModel?.total || 0) * 100)}
          </div>
          
          <div>New premium EQ:</div>
          <div className="font-bold text-right">
            {asCurrencyFormat(group.policyDetailModel?.newPremiumEquivalent || 0)}
          </div>

          <div>New premium increase W/ RR:</div>
          <div className="font-bold text-right">{!hasReliefAvailable ? "Not eligible" : asCurrencyFormat(availableRates[visibleRatesIndex]!.newPremEqWrr || 0)}</div>

          <div>RR increase:</div>
          <div className="font-bold text-right">{!hasReliefAvailable ? "Not eligible" : asPercentageValue((availableRates[visibleRatesIndex]!.rrIncrease || 0) * 100)}</div>
          
          <div>Actuarial RR discount:</div>
          <div className="font-bold text-right">{!hasReliefAvailable ? "Not eligible" : asPercentageValue((availableRates[visibleRatesIndex]!.actRrDiscount || 0) * 100)}</div>
          
          <div>Bank amount used:</div>
          <div className="font-bold text-right">{!hasReliefAvailable ? "Not eligible" : asCurrencyFormat(availableRates[visibleRatesIndex]!.bankAmountUsed || 0)}</div>        
        </div>
      </div>
    </section>
  )
}